import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import RecruiterLayout from '@/components/RecruiterLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { User, Building2, Mail, Phone, Linkedin } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import CompanyPeopleForm from '@/components/CompanyPeopleForm';
import { usePermissions } from '@/hooks/usePermissions';
import { CrudTable, CrudTableColumn } from '@/components/ui/crud-table';
import { DataTableFilter } from '@/components/ui/data-table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface Contact {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  designation: string | null;
  role: string | null;
  linkedin_url: string | null;
  is_primary: boolean;
  company_id: string;
  company: {
    id: string;
    name: string;
    category: string | null;
  } | null;
}

interface Company {
  id: string;
  name: string;
}

const ContactsManagement = () => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
  const [companyFilter, setCompanyFilter] = useState<string>('all');
  
  const { toast } = useToast();
  const navigate = useNavigate();
  const { can } = usePermissions();

  useEffect(() => {
    fetchContacts();
    fetchCompanies();
  }, []);

  const fetchContacts = async () => {
    try {
      const { data, error } = await supabase
        .from('company_people')
        .select(`
          *,
          company:companies(id, name, category)
        `)
        .order('name', { ascending: true });
      
      if (error) throw error;
      setContacts(data || []);
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    const { data } = await supabase
      .from('companies')
      .select('id, name')
      .order('name');
    setCompanies(data || []);
  };

  const handleEdit = (contact: Contact) => {
    setSelectedContact(contact);
    setSelectedCompanyId(contact.company_id);
    setFormOpen(true);
  };

  const handleAddNew = () => {
    setSelectedContact(null);
    setSelectedCompanyId('');
    setFormOpen(true);
  };

  const filteredContacts = companyFilter === 'all' 
    ? contacts 
    : contacts.filter(c => c.company_id === companyFilter);

  const columns: CrudTableColumn<Contact>[] = [
    {
      id: 'name',
      header: 'Name',
      accessorKey: 'name',
      cell: (row) => (
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-primary/10 rounded-full">
            <User className="h-4 w-4 text-primary" />
          </div>
          <div className="flex items-center gap-2">
            <span className="font-medium">{row.name}</span>
            {row.is_primary && (
              <Badge variant="secondary" className="text-xs">Primary</Badge>
            )}
          </div>
        </div>
      ),
      sortable: true,
    },
    {
      id: 'company',
      header: 'Company',
      accessorFn: (row) => row.company?.name || '',
      cell: (row) => row.company ? (
        <Button
          variant="link"
          className="p-0 h-auto font-normal"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/recruiter/companies/${row.company?.id}`);
          }}
        >
          <Building2 className="h-3 w-3 mr-1" />
          {row.company.name}
        </Button>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
      sortable: true,
    },
    {
      id: 'designation',
      header: 'Designation',
      accessorKey: 'designation',
      cell: (row) => row.designation || row.role || (
        <span className="text-muted-foreground">—</span>
      ),
      sortable: true,
    },
    {
      id: 'email',
      header: 'Email',
      accessorKey: 'email',
      cell: (row) => row.email ? (
        <a 
          href={`mailto:${row.email}`} 
          className="flex items-center gap-1 text-primary hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          <Mail className="h-3 w-3" />
          {row.email}
        </a>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
      sortable: true,
    },
    {
      id: 'phone',
      header: 'Phone',
      accessorKey: 'phone',
      cell: (row) => row.phone ? (
        <a 
          href={`tel:${row.phone}`} 
          className="flex items-center gap-1 text-primary hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          <Phone className="h-3 w-3" />
          {row.phone}
        </a>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
      sortable: true,
    },
    {
      id: 'linkedin',
      header: 'Links',
      accessorKey: 'linkedin_url',
      cell: (row) => row.linkedin_url ? (
        <a
          href={row.linkedin_url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center text-blue-600 hover:text-blue-800"
          onClick={(e) => e.stopPropagation()}
        >
          <Linkedin className="h-4 w-4" />
        </a>
      ) : null,
      sortable: false,
    },
  ];

  const filters: DataTableFilter[] = [
    {
      id: 'company',
      label: 'Company',
      value: companyFilter,
      onChange: setCompanyFilter,
      options: [
        { label: 'All Companies', value: 'all' },
        ...companies.map(c => ({ label: c.name, value: c.id })),
      ],
    },
  ];

  return (
    <RecruiterLayout>
      <div className="space-y-6">
        <CrudTable
          data={filteredContacts}
          keyField="id"
          columns={columns}
          tableName="company_people"
          singularName="Contact"
          title="Contacts"
          description="Manage company contacts and stakeholders"
          searchPlaceholder="Search contacts..."
          searchFields={['name', 'email', 'designation', 'company.name']}
          filters={filters}
          loading={loading}
          onRefresh={fetchContacts}
          canCreate={can.create('companies')}
          canUpdate={can.update('companies')}
          canDelete={can.delete('companies')}
          enableViewToggle={true}
          emptyMessage="No contacts found"
          emptyIcon={<User className="h-12 w-12" />}
          createButtonLabel="Add Contact"
          titleField="name"
          subtitleField="company"
          extraActions={[
            {
              label: 'Edit',
              icon: null,
              onClick: handleEdit,
            },
          ]}
          headerActions={
            can.create('companies') ? (
              <Button onClick={handleAddNew}>
                Add Contact
              </Button>
            ) : null
          }
        />
      </div>

      {/* Add/Edit Dialog - Company Selection */}
      <Dialog open={formOpen && !selectedCompanyId} onOpenChange={(open) => {
        if (!open) {
          setFormOpen(false);
          setSelectedContact(null);
          setSelectedCompanyId('');
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Select Company</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Select a company for this contact:</p>
            <Select value={selectedCompanyId} onValueChange={setSelectedCompanyId}>
              <SelectTrigger>
                <SelectValue placeholder="Select company..." />
              </SelectTrigger>
              <SelectContent>
                {companies.map(company => (
                  <SelectItem key={company.id} value={company.id}>{company.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </DialogContent>
      </Dialog>

      {/* Contact Form Dialog */}
      <CompanyPeopleForm
        companyId={selectedCompanyId}
        person={selectedContact ? {
          id: selectedContact.id,
          name: selectedContact.name,
          role: selectedContact.role,
          designation: selectedContact.designation,
          email: selectedContact.email,
          phone: selectedContact.phone,
          linkedin_url: selectedContact.linkedin_url,
          is_primary: selectedContact.is_primary,
        } : undefined}
        open={formOpen && !!selectedCompanyId}
        onOpenChange={(open) => {
          if (!open) {
            setFormOpen(false);
            setSelectedContact(null);
            setSelectedCompanyId('');
          }
        }}
        onSuccess={() => {
          setFormOpen(false);
          setSelectedContact(null);
          setSelectedCompanyId('');
          fetchContacts();
        }}
      />
    </RecruiterLayout>
  );
};

export default ContactsManagement;
